/**
 * Admin → "Palautteet" page. Lists smt_feedback rows and lets the admin
 * advance status (new → in_progress → done / archived) with an optional note.
 */
import { adminHandleFeedback, adminListFeedback, type Feedback, type FeedbackStatus } from '../api/feedback.ts';
import { currentUser } from '../state/session.ts';
import { t, getLang } from '../i18n/index.ts';
import { APIRequestError } from '../api/client.ts';

type Tab = FeedbackStatus | 'all';
const TABS: Tab[] = ['new', 'in_progress', 'done', 'archived', 'all'];
let activeTab: Tab = 'new';

export async function renderAdminFeedback(root: HTMLElement): Promise<void> {
  const u = currentUser();
  if (!u || u.role !== 'admin') {
    root.innerHTML = `<div class="simple-page"><div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(t('admin_only'))}</div></div>`;
    return;
  }

  root.innerHTML = `
    <div class="simple-page">
      <h1 class="simple-page-title">${escapeHtml(t('admin_feedback_title'))}</h1>
      <div class="simple-page-sub">${escapeHtml(t('admin_feedback_sub'))}</div>
      <div class="admin-tabs-bar" id="fbTabs">
        ${TABS.map(
          (tab) => `<button class="admin-tab-btn ${tab === activeTab ? 'active' : ''}" data-tab="${tab}">
            ${escapeHtml(t(`feedback_tab_${tab}`))}
          </button>`,
        ).join('')}
      </div>
      <div id="fbTable">${escapeHtml(t('view_admin_loading'))}</div>
    </div>
  `;

  root.querySelectorAll<HTMLButtonElement>('#fbTabs .admin-tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.tab as Tab;
      if (!tab || tab === activeTab) return;
      activeTab = tab;
      void renderAdminFeedback(root);
    });
  });

  await refreshTable(root);
}

async function refreshTable(root: HTMLElement): Promise<void> {
  const tbl = root.querySelector<HTMLDivElement>('#fbTable')!;
  tbl.textContent = t('view_admin_loading');
  try {
    const rows = await adminListFeedback(activeTab);
    if (!rows.length) {
      tbl.innerHTML = `<div class="placeholder-card">${escapeHtml(t('admin_feedback_empty'))}</div>`;
      return;
    }
    tbl.innerHTML = `
      <div class="listing-table-wrap"><table class="listing-table">
        <thead><tr>
          <th>${escapeHtml(t('view_admin_th_created'))}</th>
          <th>${escapeHtml(t('admin_feedback_th_submitter'))}</th>
          <th>${escapeHtml(t('admin_feedback_th_lang'))}</th>
          <th>${escapeHtml(t('view_admin_th_status'))}</th>
          <th>${escapeHtml(t('admin_feedback_th_message'))}</th>
          <th>${escapeHtml(t('view_admin_th_actions'))}</th>
        </tr></thead>
        <tbody>
          ${rows.map(renderRow).join('')}
        </tbody>
      </table></div>
    `;
    tbl.querySelectorAll<HTMLSelectElement>('[data-status-select]').forEach((sel) => {
      sel.addEventListener('change', () => handleChange(sel, root));
    });
  } catch (e) {
    tbl.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(formatErr(e as Error))}</div>`;
  }
}

function renderRow(f: Feedback): string {
  const submitter = f.submitter_name
    ? `${f.submitter_name}${f.submitter_email ? ` <${f.submitter_email}>` : ''}`
    : f.submitter_email || (f.submitter_user_id ? `user#${f.submitter_user_id}` : '(anonyymi)');
  return `
    <tr>
      <td>${escapeHtml(formatDate(f.created_at))}</td>
      <td>${escapeHtml(submitter)}</td>
      <td>${escapeHtml(f.lang)}</td>
      <td><span class="user-status-pill user-status-${escapeHtml(f.status)}">${escapeHtml(t('feedback_status_' + f.status))}</span></td>
      <td><pre style="white-space:pre-wrap;margin:0;font-family:inherit">${escapeHtml(f.message)}</pre>${f.handled_note ? `<div style="opacity:.7;margin-top:.5em">↳ ${escapeHtml(f.handled_note)}</div>` : ''}</td>
      <td>
        <select data-status-select data-id="${f.id}">
          ${(['new', 'in_progress', 'done', 'archived'] as FeedbackStatus[]).map(
            (s) => `<option value="${s}" ${s === f.status ? 'selected' : ''}>${escapeHtml(t('feedback_status_' + s))}</option>`,
          ).join('')}
        </select>
      </td>
    </tr>`;
}

async function handleChange(sel: HTMLSelectElement, root: HTMLElement): Promise<void> {
  const id = Number(sel.dataset.id);
  const status = sel.value as FeedbackStatus;
  const note = prompt(t('admin_feedback_note_prompt')) ?? '';
  sel.disabled = true;
  try {
    await adminHandleFeedback(id, status, note);
    await refreshTable(root);
  } catch (e) {
    alert(formatErr(e as Error));
    sel.disabled = false;
  }
}

function formatDate(s: string | undefined | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return s;
  }
}
function formatErr(e: Error): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  return e.message;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
